import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { XMarkIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import Button from '../ui/Button';
import { Input, PasswordInput, Field } from '../ui/Input';
import { toLoginEmail } from '../../utils/loginEmail';

/**
 * Instagram-style "Add another account" popup.
 *
 * Why a modal (not /login redirect):
 *   Navigating to /login while an authenticated session exists just bounces
 *   back to /dashboard (protected-route redirect). The clean UX is to add the
 *   new account without leaving the current page — this modal calls
 *   `addAccount(email, password)` which reuses the standard /auth/login flow
 *   without clearing the previously saved account's refresh token.
 *
 * After a successful sign-in the new account becomes the active user (Instagram
 * behavior) — the previous one stays saved in the switcher.
 */
export default function AddAccountModal({ open, onClose, onAdded }) {
  const { addAccount } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setPassword('');
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSubmitting(true);
    try {
      /* "gaurav" signs in as gaurav@digitalpresencematters.com. */
      const u = await addAccount(toLoginEmail(email), password);
      toast.success(`Signed in as ${u.name || u.email}`);
      onAdded?.(u);
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdrop = () => { if (!submitting) onClose?.(); };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[80] bg-dark-950/40 backdrop-blur-sm" onMouseDown={handleBackdrop} />
      <div
        className="fixed inset-0 z-[90] flex items-center justify-center p-4"
        onMouseDown={handleBackdrop}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-account-title"
      >
        <div
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0 flex items-center gap-2.5">
              <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-white flex items-center justify-center shadow-md shadow-brand-500/20 shrink-0">
                <UserPlusIcon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h3 id="add-account-title" className="text-sm font-semibold text-dark-900">Add another account</h3>
                <p className="text-2xs text-surface-500 mt-0.5">Your current account stays signed in.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 h-8 w-8 rounded-full text-surface-400 hover:text-dark-900 hover:bg-surface-100 flex items-center justify-center"
              title="Close"
              disabled={submitting}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <Field label="Email or username" required>
              <Input
                type="text"
                inputMode="email"
                autoCapitalize="none"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter username or email"
                required
                autoFocus
              />
            </Field>
            <Field label="Password" required>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
              />
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button type="submit" disabled={submitting || !email || !password}>
                {submitting ? 'Signing in…' : 'Add account'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body,
  );
}
